import { useState } from "react";
import { useMutation } from "convex/react";
import { useQuery } from "@/lib/convex";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Separator,
  Stack,
  Text,
} from "@chakra-ui/react";
import { LuCirclePause, LuCirclePlay } from "react-icons/lu";
import { api } from "@convex/_generated/api";
import { readableError } from "@/lib/errors";
import type { Id } from "@convex/_generated/dataModel";
import { MultiSelectTags } from "../components/MultiSelectTags";
import { PageHeader } from "../components/PageHeader";
import { toastError, toastSuccess } from "../components/ui/toaster";
import type { PersonOption } from "./ActivityAdminShared";

function formatDay(day: string) {
  return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" })
    .format(new Date(`${day}T12:00:00Z`));
}

/**
 * Who is on leave, and for how long.
 *
 * Under people rather than under activities. Leave is a fact about somebody's
 * employment — parental leave, long illness, a secondment — that HR holds and
 * the activity module merely reacts to. Putting the control on the assignment
 * page implied the opposite: that pausing was an activity-admin decision about
 * deadlines, when the deadlines move because the person is away.
 *
 * What it does is still entirely within the activity module: while somebody is
 * paused nothing of theirs is due, overdue, or waiting on anybody, and
 * resuming moves everything still outstanding by however long the pause
 * actually ran.
 */
export function PeopleLeavePage() {
  const directory = useQuery(api.persons.listCeMMies, {});
  const persons: PersonOption[] | undefined = directory?.persons;
  const pauses = useQuery(api.personActivities.listOpenPauses, {});
  const pausePerson = useMutation(api.personActivities.pausePerson);
  const resumePerson = useMutation(api.personActivities.resumePerson);

  const [personId, setPersonId] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const pausedIds = new Set((pauses ?? []).map((pause) => String(pause.personId)));

  async function handlePause() {
    if (personId === null) {
      toastError("Choose who is going on leave.");
      return;
    }
    if (reason.trim() === "") {
      toastError("A pause needs a reason.");
      return;
    }
    setBusy(true);
    try {
      const result = await pausePerson({
        personId: personId as Id<"persons">,
        reason: reason.trim(),
        startedOnDay: null,
      });
      toastSuccess(
        "Paused",
        `${result.paused} ${result.paused === 1 ? "activity" : "activities"} suspended.`,
      );
      setPersonId(null);
      setReason("");
    } catch (error) {
      // The sentence first; the browser's own words second, where they
      // add anything. On their own they tell the reader nothing.
      const readable = readableError(error, "The pause could not be recorded");
      toastError(readable.message, readable.detail);
    } finally {
      setBusy(false);
    }
  }

  async function handleResume(pausedPersonId: Id<"persons">, name: string) {
    setBusy(true);
    try {
      const result = await resumePerson({ personId: pausedPersonId, endedOnDay: null });
      toastSuccess(
        `${name} is back`,
        result.shiftedDays === 0
          ? "Nothing moved — the pause was same-day."
          : `Remaining dates moved ${result.shiftedDays} day${result.shiftedDays === 1 ? "" : "s"}.`,
      );
    } catch (error) {
      // The sentence first; the browser's own words second, where they
      // add anything. On their own they tell the reader nothing.
      const readable = readableError(error, "The resume could not be recorded");
      toastError(readable.message, readable.detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title="Leave"
        description="Who is away, and since when. Nothing of theirs is due or overdue while they are; coming back moves what is left by however long they were gone."
        breadcrumbs={[
          { label: "CeMMbase", to: "/" },
          { label: "CeMMies", to: "/cemmies" },
          { label: "Leave" },
        ]}
        actions={pauses && pauses.length > 0
          ? (
            <Badge variant="outline" px={3} py={1.5} textTransform="uppercase" letterSpacing="0.16em">
              {pauses.length} away
            </Badge>
          )
          : undefined}
      />

      <Stack gap={6} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }} maxW="4xl">
        {pauses === undefined ? (
          <Text fontSize="sm" color="fg.muted">Loading…</Text>
        ) : pauses.length === 0 ? (
          <Text fontSize="sm" color="fg.muted">Nobody is on leave.</Text>
        ) : (
          <Stack gap={2}>
            {pauses.map((pause) => (
              <Flex
                key={String(pause._id)}
                borderWidth="1px"
                borderColor="border"
                bg="bg"
                px={4}
                py={3}
                gap={4}
                align="center"
                justify="space-between"
                flexWrap="wrap"
              >
                <Stack gap={1} minW={0}>
                  <HStack gap={2} flexWrap="wrap">
                    <Text fontSize="sm" fontWeight="medium">{pause.personName}</Text>
                    <Badge size="sm" variant="subtle" colorPalette="gray">
                      {pause.daysSoFar} day{pause.daysSoFar === 1 ? "" : "s"}
                    </Badge>
                  </HStack>
                  <Text fontSize="xs" color="fg.muted">
                    Since {formatDay(pause.startedOnDay)} · {pause.reason} · recorded by {pause.pausedByName}
                  </Text>
                </Stack>
                <Button
                  size="sm"
                  variant="outline"
                  loading={busy}
                  onClick={() => void handleResume(pause.personId, pause.personName)}
                >
                  <LuCirclePlay />
                  Back at work
                </Button>
              </Flex>
            ))}
          </Stack>
        )}

        <Separator />

        <Stack gap={3} maxW="2xl">
          <Text fontSize="sm" fontWeight="medium">Record a leave</Text>
          {persons === undefined ? (
            <Text fontSize="sm" color="fg.muted">Loading people…</Text>
          ) : (
            <MultiSelectTags
              label="Who is going away"
              placeholder="Search by name or address…"
              emptyMessage="Nobody matches that."
              options={persons
                .filter((person) => !pausedIds.has(String(person._id)))
                .map((person) => ({
                  value: String(person._id),
                  label: person.fullName,
                  hint: person.email ?? undefined,
                }))}
              selected={personId === null ? [] : [personId]}
              onChange={(next) => setPersonId(next.length === 0 ? null : next[next.length - 1]!)}
            />
          )}
          <Input
            size="sm"
            placeholder="Reason — parental leave, long illness, secondment"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <Box>
            <Button size="sm" colorPalette="orange" loading={busy} onClick={() => void handlePause()}>
              <LuCirclePause />
              Record leave
            </Button>
          </Box>
          <Text fontSize="xs" color="fg.muted">
            No end date is asked for. The end of a leave is rarely known when it starts, so the
            shift is worked out from how long it actually ran.
          </Text>
        </Stack>
      </Stack>
    </Box>
  );
}
